import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Header } from "../components/layout/Header";
import { api, API_BASE, apiClient } from "../services/api";
import "./MascotaProtegida.css";

const PLACEHOLDER_IMG = "/assets/smartpet-default.jpg";

// ========== UTILIDADES DE IMAGEN ==========
const getImageUrl = (url, fallback = PLACEHOLDER_IMG) => {
    if (!url) return fallback;
    if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("/")) return url;
    const base = API_BASE.endsWith("/") ? API_BASE.slice(0, -1) : API_BASE;
    return `${base}/${url.replace(/^\/+/, "")}`;
};

const withCacheBust = (url, version) => {
    if (!url) return url;
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}v=${version || Date.now()}`;
};

// ========== GEOCODIFICACIÓN GRATUITA (OpenStreetMap) ==========
const getDireccionAproximada = async (latitud, longitud) => {
    try {
        const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitud}&lon=${longitud}&zoom=16&addressdetails=1`,
            { headers: { "Accept-Language": "es" } }
        );
        if (!res.ok) return "";
        const data = await res.json();
        if (!data?.display_name) return "";
        const calle = data.address?.road || data.address?.pedestrian || "";
        const numero = data.address?.house_number || "";
        const ciudad = data.address?.city || data.address?.town || data.address?.village || data.address?.state || "";
        if (calle && numero) return `${calle} ${numero}${ciudad ? `, ${ciudad}` : ""}`;
        if (calle) return `${calle}${ciudad ? `, ${ciudad}` : ""}`;
        return data.display_name.split(",").slice(0, 3).join(",").trim();
    } catch {
        return "";
    }
};

// ========== COMPONENTE PRINCIPAL ==========
function MascotaProtegida() {
    const { id } = useParams();
    const ubicacionEnviadaRef = useRef(false);

    const [mascota, setMascota] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [ubicacionEstado, setUbicacionEstado] = useState({
        intentando: false,
        enviada: false,
        omitida: false,
        error: null,
    });

    // ---- Funciones auxiliares ----
    const calcularEdad = (fechaNac) => {
        if (!fechaNac) return "Desconocida";
        const fechaNacimiento = new Date(fechaNac);
        if (isNaN(fechaNacimiento.getTime())) return "Desconocida";
        const hoy = new Date();
        let edad = hoy.getFullYear() - fechaNacimiento.getFullYear();
        const mesDiferencia = hoy.getMonth() - fechaNacimiento.getMonth();
        if (mesDiferencia < 0 || (mesDiferencia === 0 && hoy.getDate() < fechaNacimiento.getDate())) edad--;
        if (edad < 1) {
            let meses = mesDiferencia;
            if (meses < 0) meses += 12;
            if (meses <= 0) return "Menos de 1 mes";
            return `${meses} mes${meses !== 1 ? "es" : ""}`;
        }
        return `${edad} año${edad !== 1 ? "s" : ""}`;
    };

    const obtenerSexo = (sexo) => {
        if (sexo === 1 || sexo === "1") return { texto: "Hembra", iconoClass: "bi-gender-female", color: "#ff69b4" };
        if (sexo === 0 || sexo === "0") return { texto: "Macho", iconoClass: "bi-gender-male", color: "#3498db" };
        return { texto: "No definido", iconoClass: "bi-gender-ambiguous", color: "#9b59b6" };
    };

    const limpiarTelefono = (telefono) => {
        if (!telefono) return "";
        return String(telefono).replace(/[^\d]/g, "");
    };

    const getWhatsappLink = (telefono, mensaje) => {
        const tel = limpiarTelefono(telefono);
        if (!tel) return "#";
        const telefonoFinal = tel.startsWith("54") ? tel : `549${tel}`;
        return `https://wa.me/${telefonoFinal}?text=${encodeURIComponent(mensaje || "Hola, encontré esta mascota.")}`;
    };

    const getPhoneLink = (telefono) => {
        const tel = limpiarTelefono(telefono);
        return tel ? `tel:${tel}` : "#";
    };

    const getInstagramLink = (ig) => {
        if (!ig) return "#";
        return `https://instagram.com/${String(ig).replace("@", "").trim()}`;
    };

    // ---- CARGAR MASCOTA ----
    const cargarDatosMascota = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await apiClient.get("/mascotas", { params: { codigo_id: id } });
            const data = res.data?.mascota || res.data?.data || res.data;
            const mascotaEncontrada = Array.isArray(data) ? data[0] : data;
            if (!mascotaEncontrada || !mascotaEncontrada.id) {
                setError("El código no está vinculado a ninguna mascota.");
                setMascota(null);
                return;
            }
            setMascota(mascotaEncontrada);
        } catch (err) {
            console.error("Error al cargar mascota:", err);
            if (err.response?.status === 404) {
                setError("El código no está vinculado a ninguna mascota.");
            } else {
                setError(err.response?.data?.error || "Error de conexión. Intente nuevamente.");
            }
            setMascota(null);
        } finally {
            setLoading(false);
        }
    };

    // ---- ENVIAR UBICACIÓN ----
    const enviarUbicacion = async () => {
        if (!id || !mascota?.id) return;
        if (!("geolocation" in navigator)) {
            setUbicacionEstado({ intentando: false, enviada: false, omitida: false, error: "El navegador no soporta geolocalización." });
            return;
        }
        setUbicacionEstado({ intentando: true, enviada: false, omitida: false, error: null });
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const latitud = position.coords.latitude;
                const longitud = position.coords.longitude;
                const ubicacion = `${latitud},${longitud}`;
                try {
                    const direccionAproximada = await getDireccionAproximada(latitud, longitud);
                    await api.createUbicacion({
                        id_mascota: mascota.id,
                        ubicacion,
                        lugar: direccionAproximada || ubicacion,
                    });
                    setUbicacionEstado({ intentando: false, enviada: true, omitida: false, error: null });
                } catch (err) {
                    console.error("Error al enviar ubicación:", err.response?.data || err.message);
                    setUbicacionEstado({ intentando: false, enviada: false, omitida: false, error: err.response?.data?.error || err.message });
                }
            },
            (geoError) => {
                console.warn("No se pudo obtener ubicación:", geoError);
                setUbicacionEstado({ intentando: false, enviada: false, omitida: false, error: "No se pudo obtener la ubicación." });
            },
            { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
        );
    };

    useEffect(() => { cargarDatosMascota(); }, [id]);

    useEffect(() => {
        if (!id || ubicacionEnviadaRef.current || !mascota) return;
        ubicacionEnviadaRef.current = true;
        enviarUbicacion();
    }, [id, mascota]);

    const sexoInfo = useMemo(() => obtenerSexo(mascota?.sexo), [mascota?.sexo]);
    const edadTexto = useMemo(() => (mascota?.fecha_nacimiento ? calcularEdad(mascota.fecha_nacimiento) : "Desconocida"), [mascota?.fecha_nacimiento]);
    const imagenSrc = useMemo(() => {
        const baseUrl = getImageUrl(mascota?.urlImg, PLACEHOLDER_IMG);
        const version = mascota?.updated_at || mascota?.urlImg || Date.now();
        return withCacheBust(baseUrl, version);
    }, [mascota?.urlImg, mascota?.updated_at]);

    // ---- Render ----
    if (loading) {
        return (
            <>
                <Header />
                <main className="pet-page">
                    <div className="pet-shell">
                        <div className="pet-skeleton"></div>
                    </div>
                </main>
            </>
        );
    }

    if (error || !mascota) {
        return (
            <>
                <Header />
                <main className="pet-page">
                    <div className="pet-shell">
                        <div className="pet-error-card">
                            <h2>Este código no está registrado</h2>
                            <p>{error || "No se encontró ninguna mascota asociada a este código."}</p>
                            <Link to="/" className="pet-btn pet-btn-primary">Volver al inicio</Link>
                        </div>
                    </div>
                </main>
            </>
        );
    }

    return (
        <>
            <Header />
            <main className="pet-page">
                <div className="pet-shell">
                    {/* ===== TARJETA PRINCIPAL ===== */}
                    <section className="pet-card-main">
                        <div className="pet-image-wrap">
                            <img
                                src={imagenSrc}
                                alt={`Foto de ${mascota.nombre || "la mascota"}`}
                                className="pet-image"
                                loading="eager"
                                onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = PLACEHOLDER_IMG;
                                }}
                            />
                        </div>

                        <div className="pet-content">
                            <h1 className="pet-name">Hola, soy {mascota.nombre || "Mascota"}</h1>

                            <div className="pet-tags">
                                <span className="pet-tag">
                                    <span className="pet-tag-icon">
                                        <i className={`bi ${sexoInfo.iconoClass}`} style={{ color: sexoInfo.color }}></i>
                                    </span>
                                    {sexoInfo.texto}
                                </span>
                                <span className="pet-tag">
                                    <span className="pet-tag-icon">
                                        <i className="bi bi-heart-pulse-fill" style={{ color: "#e74c3c" }}></i>
                                    </span>
                                    {edadTexto}
                                </span>
                                {mascota.direccion && (
                                    <span className="pet-tag">
                                        <span className="pet-tag-icon">
                                            <i className="bi bi-geo-alt-fill" style={{ color: "#3498db" }}></i>
                                        </span>
                                        {mascota.direccion}
                                    </span>
                                )}
                            </div>
                        </div>
                    </section>

                    {/* ===== DESCRIPCIÓN ===== */}
                    <section>
                        <div className="pet-description">
                            <p>{mascota.descripcion || "No hay descripción adicional."}</p>
                        </div>
                    </section>

                    {/* ===== SECCIÓN DE CONTACTOS ===== */}
                    <section className="pet-contacts">
                        <div className="pet-contacts-heading">
                            <h2><i className="bi bi-people-fill" style={{ color: "#6C5C94", marginRight: "0.4rem" }}></i>Contactos</h2>
                            <p>Podés comunicarte con cualquiera de estas personas.</p>
                        </div>

                        <div className="pet-contacts-grid">
                            {mascota.persona1 && (
                                <article className="pet-contact-card">
                                    <div className="pet-contact-header">
                                        <div className="pet-contact-avatar">
                                            <i className="bi bi-person-fill"></i>
                                        </div>
                                        <div>
                                            <h3>{mascota.persona1}</h3>
                                            <p>Contacto principal</p>
                                        </div>
                                    </div>

                                    <div className="pet-contact-actions">
                                        {mascota.persona1tel && (
                                            <>
                                                <a
                                                    href={getWhatsappLink(mascota.persona1tel, mascota.mensajeRescate)}
                                                    className="pet-btn-small pet-btn-whatsapp"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-tooltip="Abrir WhatsApp"
                                                >
                                                    <i className="bi bi-whatsapp"></i> WhatsApp
                                                </a>
                                                <a
                                                    href={getPhoneLink(mascota.persona1tel)}
                                                    className="pet-btn-small pet-btn-call"
                                                    data-tooltip="Llamar ahora"
                                                >
                                                    <i className="bi bi-telephone-fill"></i> Llamar
                                                </a>
                                            </>
                                        )}
                                        {mascota.persona1ig && (
                                            <a
                                                href={getInstagramLink(mascota.persona1ig)}
                                                className="pet-btn-small pet-btn-instagram"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-tooltip="Ver perfil"
                                            >
                                                <i className="bi bi-instagram"></i> Instagram
                                            </a>
                                        )}
                                    </div>
                                </article>
                            )}

                            {mascota.persona2 && (
                                <article className="pet-contact-card">
                                    <div className="pet-contact-header">
                                        <div className="pet-contact-avatar">
                                            <i className="bi bi-person-fill"></i>
                                        </div>
                                        <div>
                                            <h3>{mascota.persona2}</h3>
                                            <p>Contacto alternativo</p>
                                        </div>
                                    </div>

                                    <div className="pet-contact-actions">
                                        {mascota.persona2tel && (
                                            <>
                                                <a
                                                    href={getWhatsappLink(mascota.persona2tel, mascota.mensajeRescate)}
                                                    className="pet-btn-small pet-btn-whatsapp"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    data-tooltip="Abrir WhatsApp"
                                                >
                                                    <i className="bi bi-whatsapp"></i> WhatsApp
                                                </a>
                                                <a
                                                    href={getPhoneLink(mascota.persona2tel)}
                                                    className="pet-btn-small pet-btn-call"
                                                    data-tooltip="Llamar ahora"
                                                >
                                                    <i className="bi bi-telephone-fill"></i> Llamar
                                                </a>
                                            </>
                                        )}
                                        {mascota.persona2ig && (
                                            <a
                                                href={getInstagramLink(mascota.persona2ig)}
                                                className="pet-btn-small pet-btn-instagram"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-tooltip="Ver perfil"
                                            >
                                                <i className="bi bi-instagram"></i> Instagram
                                            </a>
                                        )}
                                    </div>
                                </article>
                            )}
                        </div>
                    </section>
                </div>
            </main>

            <footer className="pet-footer">
                <div className="pet-footer-content">
                    <h3>Smart<span>Pet</span></h3>
                    <p>Ayudando a que cada mascota vuelva a casa 🐾</p>
                </div>
            </footer>
        </>
    );
}

export default MascotaProtegida;